/* global rwgps, Routes */
import React from 'react'
import {
  leftSidebarProps,
  rightSidebarProps,
  cueReviewProps,
  routeSplitterProps,
  loadExistingModalProps,
  saveRouteProps,
  hotkeys
} from './mappings'
import fetchJSON from 'lib/fetchJSON'
import { urlWithPrivacyCode } from 'lib/utils'
import _ from 'lodash'
import queryString from 'query-string'

export class LegacyPlannerEngine extends React.PureComponent {
  componentDidMount () {
    this.init()
    this.props.forceUpdate()
  }

  componentDidUpdate (prevProps) {
    const { heatLayer, bikeLayer, overlay, sgCollapse, mapSettings } = this.props
    if (prevProps.heatLayer !== heatLayer) {
      this.updateHeatState()
      heatLayer
        ? Routes.activeMap.showHeatLayer()
        : Routes.activeMap.hideHeatLayer()
    }
    if (prevProps.bikeLayer !== bikeLayer) {
      bikeLayer
        ? Routes.activeMap.showBikeLayer()
        : Routes.activeMap.hideBikeLayer()
    }
    if (prevProps.overlay !== overlay) {
      Routes.activeMap.gmap.setMapTypeId(overlay.id)
    }
    if (prevProps.mapSettings.showDistanceMarkers !== mapSettings.showDistanceMarkers) {
      // TODO to this for all map settings changes (move them out of
      // planner/Settings) because this is a legacy specifc concern
      Routes.activeMap.setOption('show_distance_markers', mapSettings.showDistanceMarkers)
    }
    if (prevProps.mapSettings.showCueIcons !== mapSettings.showCueIcons) {
      // TODO to this for all map settings changes (move them out of
      // planner/Settings) because this is a legacy specifc concern
      Routes.activeMap.setOption('show_coursepoint_icons', mapSettings.showCueIcons)
    }
    if (prevProps.sgCollapse !== sgCollapse) {
      sgCollapse
        ? Routes.activeMap.hideElevation()
        : Routes.activeMap.showElevation()
    }
  }

  leftSidebarProps = leftSidebarProps.bind(this)
  rightSidebarProps = rightSidebarProps.bind(this)
  cueReviewProps = cueReviewProps.bind(this)
  routeSplitterProps = routeSplitterProps.bind(this)
  loadExistingModalProps = loadExistingModalProps.bind(this)
  saveRouteProps = saveRouteProps.bind(this)
  hotkeys = hotkeys.bind(this)

  init = () => {
    const { overlay, preferences, heatLayer } = this.props
    const { plannerOpts } = rwgps.planner

    Routes.New.init({ ...plannerOpts, overlay: overlay.id }, preferences)
    this.loadInitialData()
    heatLayer && Routes.activeMap.showHeatLayer()
    this.updateHeatState()
  }

  loadInitialData = () => {
    const { objectType, trouteId } = rwgps.planner?.troute
    const { eventRouteIds } = rwgps.planner
    const { center, zoom, importType, importId, privacy_code: privacyCode } = queryString.parse(window.location.search)
    let lat, lng, latLngCenter
    if (center) {
      [lat, lng] = center?.split(',')
      latLngCenter = { lat: parseFloat(lat), lng: parseFloat(lng) }
    }

    if (objectType && trouteId) {
      fetchJSON(`/${objectType}s/${trouteId}.json`).then(r => {
        Routes.activeMap.callWhenReady(() => {
          Routes.activeMap.loadRouteForEdit(r, false, true)
          _.defer(() => {
            if (zoom) Routes.activeMap.gmap.setZoom(parseInt(zoom, 10))
            if (center) Routes.activeMap.gmap.setCenter(latLngCenter)
          })
        })
      })
    }

    if (importType && importId) {
      this.loadExisting(importId, importType, {
        ignorePrivileges: true,
        onSuccess: () => {
          _.defer(() => {
            if (zoom) Routes.activeMap.gmap.setZoom(parseInt(zoom, 10))
            if (center) Routes.activeMap.gmap.setCenter(latLngCenter)
          })
        },
        privacyCode
      })
    }

    if (eventRouteIds) {
      _.each(eventRouteIds, id => {
        fetchJSON(`/routes/${id}.json`).then(r => {
          Routes.activeMap.callWhenReady(() => {
            r.drawInactive = false
            Routes.activeMap.loadRouteForEdit(r, false)
          })
        })
      })
      Routes.activeMap.snapshot()
    }
  }

  loadExisting = (id, type, options) => {
    options = options || {}
    if (!options.ignorePrivileges && !rwgps.config.privileges['advanced_planning']) return
    if (id === null || id === undefined || id === '') return

    const onSuccess = options.onSuccess
    const privacyCode = options.privacyCode

    var urlRoot = 'routes'
    if (type === 'trip') {
      urlRoot = 'trips'
    }

    const map = Routes.activeMap

    fetchJSON(urlWithPrivacyCode(`/${urlRoot}/${id}.json`, privacyCode)).then((data) => {
      // need to get rid of these photo id refs otherwise the planner will carry them along to the save form
      // which will result in copying the photos to the saved route
      data.photo_ids = []
      data.points_of_interest.forEach(poi => { poi.photo_ids = null })
      if (type === 'trip') {
        // if type === 'trip', then we need to make sure not to include trippy attributes in the route planner
        data.track_points.forEach(tp => { ['t', 's', 'h', 'T', 'c', 'p'].forEach(attr => delete tp[attr]) })
      }

      var route = new rwgps.Route(data, map, { isTrip: type === 'trip' })
      // only attempt to load the route if it has a non-empty track
      // if it has an empty track, but contains poi, load them onto
      // the existing active route.
      // this allows folks with ACA files (one file with track, one
      // file with POI) to merge their files together
      if (route.trackPoints().length) {
        map.setActiveRoute(route)
        map.snapshot({ doSync: true })
        map.recenterMap()
      } else if (data.points_of_interest.length) {
        if (data.points_of_interest.length > 0) {
          const pois = rwgps.PointOfInterest.fromJSON(data.points_of_interest, false)
          map.activeRoute.pois = map.activeRoute.pois.concat(pois)
          map.loadPois(map.activeRoute.pois)
        }
      }
      if (onSuccess) onSuccess(route)
    })
  }

  updateHeatState = () => {
    const { heatLayer } = this.props
    Routes.heatmapEnabled = heatLayer
    if (Routes.activeMap && Routes.activeMap.activeRoute) {
      Routes.activeMap.activeRoute.redraw()
    }
  }

  showHeatLayer = () => Routes.activeMap.showHeatLayer()

  render () {
    return (
    <>
      <div id='viewport' style={{ width: '100%', height: '80%' }} />
      <div id='elevation_profile' style={{ height: '20%' }}>
        <canvas id='graph' />
      </div>
    </>
    )
  }
}
